import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatDate } from '../ui/format';
import { Badge, PageHeader, Panel, type BadgeTone } from '../ui/Page';
import { DataTable, type Column } from '../ui/DataTable';
import { Link } from 'react-router-dom';
import { createProject, listProjects, updateProject, type ProjectRow } from './api';

/** Projects: the containers tasks live in (context.md sections 33-40). */

const HEALTH_TONE: Record<string, BadgeTone> = {
  ON_TRACK: 'positive',
  AT_RISK: 'warning',
  OFF_TRACK: 'danger',
};

const TONE: Record<string, BadgeTone> = {
  ACTIVE: 'info',
  ON_HOLD: 'warning',
  COMPLETED: 'positive',
  ARCHIVED: 'neutral',
};

export function ProjectsPage(): React.JSX.Element {
  const client = useQueryClient();
  const [name, setName] = useState('');
  const [usesSprints, setUsesSprints] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const projects = useQuery({ queryKey: ['projects'], queryFn: listProjects });

  const change = useMutation({
    mutationFn: (input: { id: string; usesSprints: boolean }) =>
      updateProject(input.id, { usesSprints: input.usesSprints }),
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['projects'] });
      void client.invalidateQueries({ queryKey: ['backlog'] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const create = useMutation({
    mutationFn: () => createProject({ name: name.trim(), usesSprints }),
    onSuccess: () => {
      setName('');
      setError(null);
      void client.invalidateQueries({ queryKey: ['projects'] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const columns: Column<ProjectRow>[] = [
    {
      key: 'name',
      header: t('work.project'),
      // A link, because the project now has a page: the row is the way in, not the whole record.
      render: (project) => (
        <span className="cell-stack">
          <span className="cell-inline">
            <span
              className="cell-dot"
              style={{ background: project.color ?? 'var(--color-accent)' }}
            />
            <Link className="strong" to={`/trabalho/projetos/${project.id}`}>
              {project.name}
            </Link>
          </span>
          {project.description && <span className="muted small">{project.description}</span>}
        </span>
      ),
    },
    {
      key: 'key',
      header: t('work.key'),
      render: (project) =>
        project.key ? (
          <code className="backlog__key">{project.key}</code>
        ) : (
          <span className="muted">—</span>
        ),
    },
    {
      /*
       * The switch that decides whether this project has a backlog at all.
       *
       * In the row rather than behind an edit screen: whether a team plans in sprints is a fact
       * about how they work, and it is the first thing somebody sets up and rarely touches again.
       */
      key: 'sprints',
      header: t('work.usesSprints'),
      render: (project) => (
        <span className="cell-inline">
          <label className="cell-inline">
            <input
              type="checkbox"
              checked={project.uses_sprints}
              disabled={change.isPending}
              aria-label={t('work.usesSprints')}
              onChange={(event) =>
                change.mutate({ id: project.id, usesSprints: event.target.checked })
              }
            />
            <span className="muted small">
              {project.uses_sprints ? t('common.yes') : t('common.no')}
            </span>
          </label>

          {project.uses_sprints && (
            <Link className="small" to={`/trabalho/backlog?project=${project.id}`}>
              {t('sprint.backlog')}
            </Link>
          )}
        </span>
      ),
    },
    {
      key: 'status',
      header: t('crm.field.status'),
      render: (project) => (
        <Badge tone={TONE[project.status] ?? 'neutral'}>{t(`work.status.${project.status}`)}</Badge>
      ),
    },
    {
      key: 'health',
      header: t('work.health'),
      render: (project) => (
        <Badge tone={HEALTH_TONE[project.health] ?? 'neutral'}>
          {t(`work.health.${project.health}`)}
        </Badge>
      ),
    },
    {
      key: 'priority',
      header: t('work.priority'),
      secondary: true,
      render: (project) => t(`work.priority.${project.priority}`),
    },
    {
      /*
       * Counted from the tasks, never typed.
       *
       * The overdue count rides alongside because a project at eighty per cent with nine late
       * tasks is not a project at eighty per cent, and the bar alone says it is.
       */
      key: 'progress',
      header: t('work.progress'),
      render: (project) => {
        const total = project.progress?.tasks ?? 0;
        const done = project.progress?.done ?? 0;
        const percent = total === 0 ? 0 : Math.round((done / total) * 100);

        return total === 0 ? (
          <span className="muted">—</span>
        ) : (
          <span className="cell-stack">
            <span className="project-progress__bar" aria-hidden="true">
              <span style={{ inlineSize: `${percent}%` }} />
            </span>
            <span className="muted small">
              {done}/{total}
              {project.progress.overdue > 0 &&
                ` · ${t('work.overdueShort').replace('{n}', String(project.progress.overdue))}`}
            </span>
          </span>
        );
      },
    },
    {
      key: 'due',
      header: t('work.due'),
      secondary: true,
      render: (project) => formatDate(project.due_date),
    },
    {
      key: 'created',
      header: t('common.createdAt'),
      secondary: true,
      align: 'end',
      render: (project) => formatDate(project.created_at),
    },
  ];

  return (
    <div className="view">
      <PageHeader title={t('nav.projects')} description={t('work.projectsDescription')} />

      <form
        className="toolbar"
        onSubmit={(event) => {
          event.preventDefault();
          if (name.trim()) create.mutate();
        }}
      >
        <label className="field-search">
          <input
            value={name}
            placeholder={t('work.newProjectPlaceholder')}
            onChange={(event) => setName(event.target.value)}
          />
        </label>

        <label className="cell-inline">
          <input
            type="checkbox"
            checked={usesSprints}
            onChange={(event) => setUsesSprints(event.target.checked)}
          />
          <span className="muted small">{t('work.usesSprints')}</span>
        </label>

        <button
          type="submit"
          className="button button--primary button--compact"
          disabled={name.trim() === '' || create.isPending}
        >
          {create.isPending ? t('work.creating') : t('work.newProject')}
        </button>
      </form>

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      <Panel>
        <DataTable
          tableKey="projects"
          rows={projects.data?.projects}
          columns={columns}
          rowKey={(project) => project.id}
          isLoading={projects.isLoading}
          error={projects.error}
          onRetry={() => void projects.refetch()}
          empty={{ title: t('work.noProjects'), description: t('work.noProjectsBody') }}
        />
      </Panel>
    </div>
  );
}
